import Vue from 'vue';
import pipelineApiRepository from '@/services/pipeline/PipelineApiRepository';
import customerApiRepository from '@/services/customer/CustomerApiRepository';
import { loadCardFields, saveCardFields } from '@/services/pipeline/leadFieldPrefs';

/**
 * Un campo de cualificación se puede corregir pero no vaciar: el upsert del backend se
 * salta los null y las cadenas vacías. `saveLeadField` lo rechaza con este mensaje para
 * que quien llame pueda decirlo con sus palabras en vez de mostrar un error genérico.
 */
export const EMPTY_FIELD = 'PIPELINE_EMPTY_FIELD';

/** Peticiones de campos de lead en vuelo, para que dos tarjetas no pidan lo mismo dos veces. */
const inFlightLeadFields = new Map();

/** How long a lead id stays in inFlightMoves after the API call resolves, so the WS echo is still filtered. */
const IN_FLIGHT_ECHO_GRACE_MS = 3000;
/** Debounce window for full board refetches triggered by WS events. */
const BOARD_REFETCH_DEBOUNCE_MS = 500;

let boardRefetchTimer = null;

/** @returns {string} The key used in leadsByStage: stage id (native) or stage name (Zoho, id is null) */
const stageKey = (stage) => stage.id || stage.name;

const emptyStageLeads = () => ({ items: [], page: 0, hasMore: false, loading: false, total: null });

/** Shorter queries are not sent to the API: Zoho rejects them. */
export const MIN_SEARCH_LENGTH = 2;

const emptySearch = () => ({ query: '', ids: [], hasMore: false, loading: false });

const serializeError = (error) => {
    try {
        if (!error) return null;
        if (error.response && error.response.data) {
            const data = error.response.data;
            if (typeof data === 'string') return data;
            if (data && (data.message || data.error)) return data.message || data.error;
        }
        if (error.message) return error.message;
        return String(error);
    } catch {
        return 'Unknown error';
    }
};

/**
 * Maps the snake_case lead entity carried by domain events to the camelCase LeadCard shape.
 * @param {Object} entity - Lead entity from PipelineLeadCreated / PipelineLeadMoved
 * @returns {Object} LeadCard
 */
const leadCardFromEventEntity = (entity) => ({
    id: entity.uuid,
    name: entity.name,
    phone: entity.phone,
    email: entity.email,
    stage: entity.stage,
    source: entity.source,
    customerId: entity.customer_id,
    conversationId: entity.conversation_id,
    externalUrl: null,
    dateCreated: entity.date_created,
    stageEnteredAt: entity.stage_entered_at,
});

const state = {
    source: null,
    enabled: false,
    stagesEditable: false,
    playbookSkillId: null,
    stages: [],
    leadsByStage: {},
    loading: false,
    initialized: false,
    error: null,
    inFlightMoves: [],
    /**
     * Campos de cualificación por lead. El board no los trae —el LeadCard no los tiene—,
     * así que se piden uno a uno y se quedan aquí para no repetir la llamada por cada
     * repintado de la tarjeta.
     */
    leadFieldsById: {},
    /** Claves de los campos que se asoman en las tarjetas del tablero. */
    cardFields: [],
    /**
     * La búsqueda del servidor: el tablero sólo tiene cargadas las primeras tarjetas de cada columna,
     * así que filtrarlas en el navegador no encontraba a quien estaba más abajo. `ids` son las
     * coincidencias, que se cuelan en el filtro aunque no casen en local (en Zoho la tarjeta de una
     * oportunidad no lleva el teléfono del contacto).
     */
    search: emptySearch(),
};

const getters = {
    isNative: (state) => state.source === 'NATIVE',
    isZoho: (state) => state.source === 'ZOHO_CRM',
    orderedStages: (state) => [...state.stages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    getStageKey: () => stageKey,
    stageLeads: (state) => (key) => state.leadsByStage[key] || emptyStageLeads(),
    totalLoadedLeads: (state) =>
        Object.values(state.leadsByStage).reduce((total, entry) => total + entry.items.length, 0),
    hasAnyLead: (state, getters) =>
        getters.totalLoadedLeads > 0 || state.stages.some((stage) => (stage.leadCount ?? 0) > 0),
};

const mutations = {
    SET_LEAD_FIELDS(state, { leadId, fields }) {
        Vue.set(state.leadFieldsById, leadId, fields || {});
    },
    SET_CARD_FIELDS(state, keys) {
        state.cardFields = Array.isArray(keys) ? keys : [];
    },
    SET_SEARCH(state, search) {
        state.search = { ...emptySearch(), ...search };
    },
    /** Mete las coincidencias de la búsqueda en la columna de su etapa, sin duplicar ninguna tarjeta. */
    MERGE_SEARCH_LEADS(state, leads) {
        const loadedIds = new Set(
            Object.values(state.leadsByStage).flatMap((entry) => entry.items.map((lead) => lead.id)),
        );

        leads.forEach((lead) => {
            if (loadedIds.has(lead.id)) return;

            const stage = state.stages.find((candidate) => candidate.name === lead.stage);
            if (!stage) return;

            const key = stageKey(stage);
            if (!state.leadsByStage[key]) {
                Vue.set(state.leadsByStage, key, emptyStageLeads());
            }
            state.leadsByStage[key].items = [...state.leadsByStage[key].items, lead];
            loadedIds.add(lead.id);
        });
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_ERROR(state, error) {
        state.error = serializeError(error);
    },
    SET_INITIALIZED(state, initialized) {
        state.initialized = initialized;
    },
    SET_BOARD(state, board) {
        state.source = board.source;
        state.enabled = board.enabled;
        state.stagesEditable = board.stagesEditable;
        state.playbookSkillId = board.playbookSkillId || null;

        const stages = board.stages || [];
        state.stages = stages.map((stage) => {
            const clone = { ...stage };
            delete clone.leads;
            delete clone.hasMore;
            return clone;
        });

        const leadsByStage = {};
        stages.forEach((stage) => {
            leadsByStage[stageKey(stage)] = {
                items: stage.leads || [],
                page: 0,
                hasMore: !!stage.hasMore,
                loading: false,
                total: stage.leadCount ?? null,
            };
        });
        state.leadsByStage = leadsByStage;
    },
    SET_STAGES(state, stages) {
        state.stages = stages;
    },
    SET_STAGE_LOADING(state, { key, loading }) {
        if (!state.leadsByStage[key]) {
            Vue.set(state.leadsByStage, key, emptyStageLeads());
        }
        state.leadsByStage[key].loading = loading;
    },
    SET_STAGE_LEADS(state, { key, leads, page, hasMore, total, append }) {
        if (!state.leadsByStage[key]) {
            Vue.set(state.leadsByStage, key, emptyStageLeads());
        }
        const entry = state.leadsByStage[key];
        if (append) {
            const knownIds = new Set(entry.items.map((lead) => lead.id));
            entry.items = [...entry.items, ...leads.filter((lead) => !knownIds.has(lead.id))];
        } else {
            entry.items = leads;
        }
        entry.page = page;
        entry.hasMore = hasMore;
        if (total !== undefined) entry.total = total;
    },
    /** Optimistic move: splices the card from the source list into the destination at toIndex. */
    MOVE_LEAD(state, { leadId, fromKey, toKey, toStageName, toIndex }) {
        const from = state.leadsByStage[fromKey];
        const to = state.leadsByStage[toKey];
        if (!from || !to) return;

        const index = from.items.findIndex((lead) => lead.id === leadId);
        if (index === -1) return;

        const [card] = from.items.splice(index, 1);
        const movedCard = { ...card, stage: toStageName, stageEnteredAt: Math.floor(Date.now() / 1000) };
        const insertAt = Math.min(Math.max(toIndex, 0), to.items.length);
        to.items.splice(insertAt, 0, movedCard);

        state.stages.forEach((stage) => {
            if (stage.leadCount === null || stage.leadCount === undefined) return;
            const key = stageKey(stage);
            if (key === fromKey) stage.leadCount = Math.max(0, stage.leadCount - 1);
            if (key === toKey) stage.leadCount += 1;
        });
    },
    /** Restores a card to its memoized position after a failed move. */
    ROLLBACK_MOVE(state, { leadId, fromKey, toKey, originalIndex, originalStageName, originalStageEnteredAt }) {
        const from = state.leadsByStage[fromKey];
        const to = state.leadsByStage[toKey];
        if (!from || !to) return;

        const index = to.items.findIndex((lead) => lead.id === leadId);
        if (index === -1) return;

        const [card] = to.items.splice(index, 1);
        const restored = { ...card, stage: originalStageName, stageEnteredAt: originalStageEnteredAt };
        const insertAt = Math.min(Math.max(originalIndex, 0), from.items.length);
        from.items.splice(insertAt, 0, restored);

        state.stages.forEach((stage) => {
            if (stage.leadCount === null || stage.leadCount === undefined) return;
            const key = stageKey(stage);
            if (key === fromKey) stage.leadCount += 1;
            if (key === toKey) stage.leadCount = Math.max(0, stage.leadCount - 1);
        });
    },
    /** Patches a moved card with the authoritative LeadCard returned by the API. */
    PATCH_LEAD(state, card) {
        Object.values(state.leadsByStage).forEach((entry) => {
            const index = entry.items.findIndex((lead) => lead.id === card.id);
            if (index !== -1) entry.items.splice(index, 1, card);
        });
    },
    /** Takes the card out of whatever column holds it. Returns nothing: the action memoizes first. */
    REMOVE_LEAD(state, leadId) {
        Object.keys(state.leadsByStage).forEach((key) => {
            const entry = state.leadsByStage[key];
            const index = entry.items.findIndex((lead) => lead.id === leadId);
            if (index === -1) return;

            entry.items.splice(index, 1);
            if (entry.total !== null && entry.total !== undefined) entry.total = Math.max(0, entry.total - 1);

            const stage = state.stages.find((candidate) => stageKey(candidate) === key);
            if (stage && stage.leadCount !== null && stage.leadCount !== undefined) {
                stage.leadCount = Math.max(0, stage.leadCount - 1);
            }
        });
    },
    /** Puts a card back where it was after a failed delete. */
    RESTORE_LEAD(state, { key, card, index }) {
        const entry = state.leadsByStage[key];
        if (!entry) return;

        entry.items.splice(Math.min(Math.max(index, 0), entry.items.length), 0, card);
        if (entry.total !== null && entry.total !== undefined) entry.total += 1;

        const stage = state.stages.find((candidate) => stageKey(candidate) === key);
        if (stage && stage.leadCount !== null && stage.leadCount !== undefined) stage.leadCount += 1;
    },
    ADD_IN_FLIGHT(state, leadId) {
        if (!state.inFlightMoves.includes(leadId)) {
            state.inFlightMoves.push(leadId);
        }
    },
    REMOVE_IN_FLIGHT(state, leadId) {
        state.inFlightMoves = state.inFlightMoves.filter((id) => id !== leadId);
    },
    /** Applies a WS lead event locally: removes the card wherever it is and inserts it in its stage. */
    APPLY_LEAD_EVENT(state, card) {
        let removedFromKey = null;
        Object.keys(state.leadsByStage).forEach((key) => {
            const entry = state.leadsByStage[key];
            const index = entry.items.findIndex((lead) => lead.id === card.id);
            if (index !== -1) {
                entry.items.splice(index, 1);
                removedFromKey = key;
            }
        });

        const targetStage = state.stages.find((stage) => stage.name === card.stage);
        const targetKey = targetStage ? stageKey(targetStage) : null;

        if (targetKey && state.leadsByStage[targetKey]) {
            state.leadsByStage[targetKey].items.unshift(card);
        }

        state.stages.forEach((stage) => {
            if (stage.leadCount === null || stage.leadCount === undefined) return;
            const key = stageKey(stage);
            if (key === removedFromKey) stage.leadCount = Math.max(0, stage.leadCount - 1);
            if (key === targetKey) stage.leadCount += 1;
        });
    },
    CLEAR_STATE(state) {
        state.source = null;
        state.enabled = false;
        state.stagesEditable = false;
        state.playbookSkillId = null;
        state.stages = [];
        state.leadsByStage = {};
        state.leadFieldsById = {};
        state.loading = false;
        state.initialized = false;
        state.error = null;
        state.inFlightMoves = [];
        state.search = emptySearch();
    },
};

const actions = {
    /**
     * Busca en todo el pipeline, no sólo en lo cargado. Una respuesta que llega cuando ya se está
     * buscando otra cosa se descarta: si no, una búsqueda lenta pisaría a la que se escribió después.
     */
    async searchLeads({ state, commit }, query) {
        const text = (query || '').trim();
        const searchable = text.length >= MIN_SEARCH_LENGTH;

        commit('SET_SEARCH', { query: text, loading: searchable });
        if (!searchable) return;

        try {
            const result = await pipelineApiRepository.searchLeads(text);
            if (state.search.query !== text) return;

            const leads = (result && result.leads) || [];
            commit('MERGE_SEARCH_LEADS', leads);
            commit('SET_SEARCH', { query: text, ids: leads.map((lead) => lead.id), hasMore: !!result.hasMore });
        } catch (error) {
            console.error('Error searching leads:', error);
            if (state.search.query === text) commit('SET_SEARCH', { query: text });
        }
    },

    async fetchBoard({ commit }, { size = 20 } = {}) {
        commit('SET_LOADING', true);
        try {
            const board = await pipelineApiRepository.getBoard(size);
            commit('SET_BOARD', board);
            commit('SET_ERROR', null);
            commit('SET_INITIALIZED', true);
        } catch (error) {
            console.error('Error fetching pipeline board:', error);
            commit('SET_ERROR', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * Trae los campos de cualificación de un lead una sola vez. Las tarjetas los piden al
     * pintarse, así que hay que protegerse de dos cosas: el que ya está en caché y el que
     * está pidiéndose ahora mismo por otra tarjeta.
     */
    async ensureLeadFields({ state, commit }, leadId) {
        if (!leadId || state.leadFieldsById[leadId]) return state.leadFieldsById[leadId] || {};
        if (inFlightLeadFields.has(leadId)) return inFlightLeadFields.get(leadId);

        const request = pipelineApiRepository
            .getLead(leadId)
            .then((details) => {
                const fields = details?.fields || {};
                commit('SET_LEAD_FIELDS', { leadId, fields });
                return fields;
            })
            .catch((error) => {
                console.error('Error fetching lead fields:', error);
                return {};
            })
            .finally(() => {
                inFlightLeadFields.delete(leadId);
            });

        inFlightLeadFields.set(leadId, request);
        return request;
    },

    /** Vuelve a pedir los campos de un lead que ya estaba en caché, sin vaciar lo que se ve */
    async reloadLeadFields({ commit }, leadId) {
        try {
            const details = await pipelineApiRepository.getLead(leadId);
            if (details?.fields) commit('SET_LEAD_FIELDS', { leadId, fields: details.fields });
        } catch (error) {
            console.error('Error reloading lead fields:', error);
        }
    },

    /** Los campos que ya tiene quien los haya pedido (la ficha del lead, al abrirse) */
    cacheLeadFields({ commit }, { leadId, fields }) {
        commit('SET_LEAD_FIELDS', { leadId, fields });
    },

    /**
     * Guarda UN campo de cualificación de un lead. Lo usan la ficha y la tabla.
     *
     * No hay endpoint para escribir los campos de un lead: el pipeline nativo los saca de
     * los staging assets, así que se manda un asset nuevo y el backend dispara el mismo
     * upsert que usa el asistente.
     *
     * SE MANDA SÓLO EL CAMPO TOCADO, y es importante que sea así. Probado contra la API el
     * 29/09: el upsert FUSIONA, los campos que no viajan se quedan como estaban. Mandar el
     * mapa entero «por si acaso» no es más seguro, es más peligroso: si el asistente ha
     * escrito algo mientras esta pantalla tenía los campos en caché, mandarlos todos
     * devolvería los valores viejos y se perdería lo que acaba de recoger.
     *
     * Dos cosas que no se ven y cuestan caro:
     *  - el upsert SE SALTA los null y las cadenas vacías, así que un campo se puede
     *    corregir pero NO vaciar. Se rechaza aquí en vez de dejar que parezca guardado y
     *    siga saliendo el valor viejo;
     *  - la etapa se decide por estos campos (`stageByField`), así que rellenar uno puede
     *    mover la tarjeta de columna. De ahí el refresco del tablero al terminar.
     *
     * @throws {Error} con `message === EMPTY_FIELD` si el valor queda vacío
     */
    async saveLeadField({ state, commit, dispatch }, { leadId, customerId, key, value }) {
        const clean = String(value ?? '').trim();
        if (!clean) throw new Error(EMPTY_FIELD);
        if (!customerId || !state.playbookSkillId) throw new Error('Lead sin cliente o skill del pipeline');

        const current = state.leadFieldsById[leadId] || {};

        await customerApiRepository.saveQualificationFields(customerId, state.playbookSkillId, { [key]: clean });

        commit('SET_LEAD_FIELDS', { leadId, fields: { ...current, [key]: clean } });
        // No se espera: la tarjeta ya muestra el valor nuevo y el tablero se pone al día solo.
        dispatch('fetchBoard').catch(() => {});
        return clean;
    },

    async loadCardFieldPrefs({ commit, rootState }) {
        const accountId = rootState.user?.user?.account_id;
        commit('SET_CARD_FIELDS', await loadCardFields(accountId));
    },

    async setCardFields({ commit, rootState }, keys) {
        commit('SET_CARD_FIELDS', keys);
        await saveCardFields(rootState.user?.user?.account_id, keys);
    },

    async activatePipeline({ commit }) {
        commit('SET_LOADING', true);
        try {
            const board = await pipelineApiRepository.activate();
            commit('SET_BOARD', board);
            commit('SET_ERROR', null);
            commit('SET_INITIALIZED', true);
            return board;
        } catch (error) {
            console.error('Error activating pipeline:', error);
            commit('SET_ERROR', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async fetchStageLeads({ state, commit }, { stageKey: key, reset = false, size = 20 }) {
        const entry = state.leadsByStage[key];
        if (entry && entry.loading) return;
        if (!reset && entry && !entry.hasMore) return;

        const page = reset || !entry ? 0 : entry.page + 1;
        commit('SET_STAGE_LOADING', { key, loading: true });
        try {
            const response = await pipelineApiRepository.getStageLeads(key, page, size);
            commit('SET_STAGE_LEADS', {
                key,
                leads: response.leads || [],
                page,
                hasMore: !!response.hasMore,
                total: response.total ?? undefined,
                append: !reset && page > 0,
            });
        } catch (error) {
            console.error(`Error fetching leads for stage ${key}:`, error);
            commit('SET_ERROR', error);
            throw error;
        } finally {
            commit('SET_STAGE_LOADING', { key, loading: false });
        }
    },

    /**
     * Optimistic move: the card jumps immediately, the API confirms, and a failure rolls it back.
     * The lead id stays in inFlightMoves a few seconds after resolving so the WS echo is ignored.
     */
    async moveLead({ state, commit }, { leadId, fromKey, toKey, toStageName, toIndex }) {
        const from = state.leadsByStage[fromKey];
        const original = from ? from.items.find((lead) => lead.id === leadId) : null;
        if (!original) return;

        const memo = {
            leadId,
            fromKey,
            toKey,
            originalIndex: from.items.indexOf(original),
            originalStageName: original.stage,
            originalStageEnteredAt: original.stageEnteredAt,
        };

        commit('ADD_IN_FLIGHT', leadId);
        commit('MOVE_LEAD', { leadId, fromKey, toKey, toStageName, toIndex });
        try {
            const card = await pipelineApiRepository.moveLead(leadId, toStageName);
            if (card && card.id) commit('PATCH_LEAD', card);
        } catch (error) {
            console.error('Error moving lead:', error);
            commit('ROLLBACK_MOVE', memo);
            commit('SET_ERROR', error);
            throw error;
        } finally {
            setTimeout(() => commit('REMOVE_IN_FLIGHT', leadId), IN_FLIGHT_ECHO_GRACE_MS);
        }
    },

    /**
     * Optimistic delete: the card disappears at once and comes back if the API rejects it. Shares
     * inFlightMoves with the move so the PipelineLeadDeleted echo does not double-count the column.
     */
    async deleteLead({ state, commit }, leadId) {
        let memo = null;
        Object.keys(state.leadsByStage).forEach((key) => {
            const index = state.leadsByStage[key].items.findIndex((lead) => lead.id === leadId);
            if (index !== -1) memo = { key, index, card: state.leadsByStage[key].items[index] };
        });

        commit('ADD_IN_FLIGHT', leadId);
        commit('REMOVE_LEAD', leadId);
        try {
            await pipelineApiRepository.deleteLead(leadId);
        } catch (error) {
            console.error('Error deleting lead:', error);
            if (memo) commit('RESTORE_LEAD', memo);
            commit('SET_ERROR', error);
            throw error;
        } finally {
            setTimeout(() => commit('REMOVE_IN_FLIGHT', leadId), IN_FLIGHT_ECHO_GRACE_MS);
        }
    },

    async createStage({ dispatch }, { name, color }) {
        try {
            await pipelineApiRepository.createStage(name, color);
        } catch (error) {
            console.error('Error creating stage:', error);
            throw error;
        }
        await dispatch('fetchBoard');
    },

    async renameStage({ state, commit }, { stageId, name, color }) {
        const changes = {};
        if (name !== undefined) changes.name = name;
        if (color !== undefined) changes.color = color;
        try {
            const updated = await pipelineApiRepository.updateStage(stageId, changes);
            const stages = state.stages.map((stage) => (stage.id === stageId ? { ...stage, ...updated } : stage));
            commit('SET_STAGES', stages);
        } catch (error) {
            console.error('Error updating stage:', error);
            throw error;
        }
    },

    /**
     * Colour of a stage. Works in both modes: with an external CRM the key is the stage name (there is
     * no stable stage id in Zoho) and the colour lives in the skill's configValues.
     *
     * Optimistic: the swatch has to answer instantly, and a failed colour is a cosmetic rollback.
     */
    async setStageColor({ state, commit }, { stageKey, color }) {
        const previous = state.stages;
        const matches = (stage) => stage.id === stageKey || stage.name === stageKey;

        commit(
            'SET_STAGES',
            state.stages.map((stage) => (matches(stage) ? { ...stage, color } : stage)),
        );

        try {
            await pipelineApiRepository.setStageColor(stageKey, color);
        } catch (error) {
            commit('SET_STAGES', previous);
            console.error('Error setting stage colour:', error);
            throw error;
        }
    },

    async deleteStage({ dispatch }, { stageId, migrateTo }) {
        try {
            await pipelineApiRepository.deleteStage(stageId, migrateTo);
        } catch (error) {
            console.error('Error deleting stage:', error);
            throw error;
        }
        await dispatch('fetchBoard');
    },

    /** Optimistic reorder with rollback: the columns move at once and revert if the API rejects it. */
    async reorderStages({ state, commit }, stageIds) {
        const originalStages = state.stages;
        const byId = new Map(originalStages.map((stage) => [stage.id, stage]));
        const reordered = stageIds
            .map((id, index) => {
                const stage = byId.get(id);
                return stage ? { ...stage, order: index } : null;
            })
            .filter(Boolean);
        if (reordered.length !== originalStages.length) return;

        commit('SET_STAGES', reordered);
        try {
            await pipelineApiRepository.reorderStages(stageIds);
        } catch (error) {
            console.error('Error reordering stages:', error);
            commit('SET_STAGES', originalStages);
            throw error;
        }
    },

    async subscribeToPipelineUpdates({ rootState, dispatch }) {
        const topic = `/account/${rootState.user.user.account_id}/pipeline`;
        try {
            await dispatch(
                'websocket/subscribe',
                { topic, callback: (event) => dispatch('handlePipelineEvent', event) },
                { root: true },
            );
        } catch (error) {
            console.error(`Failed to subscribe to ${topic}:`, error);
        }
    },

    unsubscribeFromPipelineUpdates({ rootState, dispatch }) {
        const topic = `/account/${rootState.user.user.account_id}/pipeline`;
        dispatch('websocket/unsubscribe', { topic }, { root: true });
    },

    handlePipelineEvent({ state, commit, dispatch }, event) {
        if (!event || !event.name) return;

        // event.data may arrive as a JSON string inside the domain-event wrapper
        let data = event.data;
        if (typeof data === 'string') {
            try {
                data = JSON.parse(data);
            } catch (error) {
                console.error('Unparseable pipeline event data:', error);
                return;
            }
        }

        switch (event.name) {
            case 'PipelineLeadCreated':
            case 'PipelineLeadMoved': {
                if (!data || !data.uuid) return;
                // Own echo of an optimistic move: the board already shows it
                if (state.inFlightMoves.includes(data.uuid)) return;
                commit('APPLY_LEAD_EVENT', leadCardFromEventEntity(data));
                // El evento también salta cuando alguien rellena un campo (el upsert lo publica
                // como un movimiento), pero no trae los campos. Si esta pantalla los tenía,
                // se vuelven a pedir: si no, quien tenga la tabla abierta seguiría viendo el
                // valor viejo hasta recargar.
                if (state.leadFieldsById[data.uuid]) dispatch('reloadLeadFields', data.uuid);
                break;
            }
            case 'PipelineLeadDeleted': {
                if (!data || !data.uuid) return;
                // Own echo of an optimistic delete: the card is already gone
                if (state.inFlightMoves.includes(data.uuid)) return;
                commit('REMOVE_LEAD', data.uuid);
                break;
            }
            case 'PipelineStagesUpdated':
            case 'PipelineBoardChanged':
                dispatch('debouncedRefetchBoard');
                break;
            default:
                console.warn(`Unhandled pipeline event: ${event.name}`);
        }
    },

    /** Collapses bursts of WS structure events into a single board refetch. */
    debouncedRefetchBoard({ dispatch }) {
        clearTimeout(boardRefetchTimer);
        boardRefetchTimer = setTimeout(() => {
            dispatch('fetchBoard').catch(() => {});
        }, BOARD_REFETCH_DEBOUNCE_MS);
    },

    clearState({ commit }) {
        clearTimeout(boardRefetchTimer);
        commit('CLEAR_STATE');
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
